import {Color} from '@benchling/nucleus-ui';
import {Button, createStyles} from '@mantine/core';
import React from 'react';

import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

const useStyles = createStyles((theme) => ({
  // Bottom bar: side + bottom borders with the same 12px bottom radius as the omnibox popup, so it
  // reads as the rounded base of the container (per Figma Frame 1000002826). A lighter GRAY_200 top
  // border acts as the divider line separating the footer from the results above it.
  root: {
    boxSizing: 'border-box' as const,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    // Fixed-height row inside the omnibox's flex column: never shrink when results fill the modal to
    // its max-height, so only the scrollable results area absorbs the overflow.
    flexShrink: 0,
    padding: '8px 16px',
    gap: 16,
    // Tall enough to seat the Share feedback button (xs Button ~30px) with even padding.
    height: 46,
    backgroundColor: Color.GRAY_50,
    borderTop: `1px solid ${Color.GRAY_200}`,
    borderRight: `1px solid ${Color.GRAY_400}`,
    borderBottom: `1px solid ${Color.GRAY_400}`,
    borderLeft: `1px solid ${Color.GRAY_400}`,
    borderRadius: '0px 0px 12px 12px',
  },
  hints: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
  },
  hint: {
    display: 'inline-flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    fontSize: theme.fontSizes.xs,
    fontWeight: theme.other.fontWeights.normal,
    lineHeight: '16px',
    color: Color.GRAY_500,
    whiteSpace: 'nowrap' as const,
  },
  // The return-arrow glyph used for "Open"; there is no equivalent BenchlingIcon, so we render the
  // unicode character sized to sit alongside the navigation arrow icons.
  returnGlyph: {
    fontSize: theme.fontSizes.xs,
    lineHeight: '16px',
  },
  // Navigation arrow icons are rendered a touch smaller than the surrounding hint text.
  navIcon: {
    fontSize: 10,
  },
  kbd: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: 16.5,
    padding: '0 4px',
    backgroundColor: Color.WHITE,
    borderRadius: 2,
    // Override the <kbd> UA default monospace font so the key label matches the hint text.
    fontFamily: 'inherit',
    fontSize: theme.fontSizes.xs,
    lineHeight: '16px',
    color: Color.GRAY_500,
  },
  // Push the feedback button to the right edge, opposite the keyboard-shortcut hints.
  feedbackButton: {
    marginLeft: 'auto',
    // Muted-gold treatment that marks this as a temporary "give us feedback" affordance rather than
    // a standard action; these values are intentionally outside the theme palette.
    borderColor: '#f0dfa0',
    backgroundColor: '#fdf6d8',
    color: '#8a6d1a',
    '&:hover': {
      backgroundColor: '#f6e9bd',
    },
  },
  feedbackIcon: {
    fontSize: 11,
  },
}));

// Airtable feedback form for the quicksearch beta.
export const FEEDBACK_FORM_URL = 'https://airtable.com/appqWkD9LsgBAWGE6/pagIaOAtKsQVxTfdW/form';

function FeedbackLink(): JSX.Element {
  const {classes} = useStyles();
  return (
    <Button
      component="a"
      href={FEEDBACK_FORM_URL}
      target="_blank"
      rel="noopener noreferrer"
      variant="secondary"
      size="xs"
      radius="md"
      className={classes.feedbackButton}
      leftIcon={
        <BenchlingIcon icon="comment-dots-outline" className={classes.feedbackIcon} aria-hidden="true" />
      }
    >
      Share feedback
    </Button>
  );
}

interface OmniboxFooterProps {
  hasNavigableResults: boolean;
  filterOptionCount?: number;
  filterShownCount?: number;
}

function IconHint({icons, label}: {icons: React.ReactNode; label: string}): JSX.Element {
  const {classes} = useStyles();
  return (
    <span className={classes.hint}>
      {icons}
      {label}
    </span>
  );
}

function KeyHint({label, children}: {label: string; children: React.ReactNode}): JSX.Element {
  const {classes} = useStyles();
  return (
    <span className={classes.hint}>
      <kbd className={classes.kbd}>{children}</kbd>
      {label}
    </span>
  );
}

export default function OmniboxFooter({
  hasNavigableResults,
  filterOptionCount,
  filterShownCount,
}: OmniboxFooterProps): JSX.Element | null {
  const {classes} = useStyles();
  const isFilterMode = filterOptionCount != null;

  if (isFilterMode) {
    return (
      <div className={classes.root}>
        <span className={classes.hint}>
          {filterShownCount} of {filterOptionCount} {filterOptionCount === 1 ? 'option' : 'options'}, type to
          narrow down
        </span>
        <div className={classes.hints}>
          <IconHint
            icons={
              <>
                <BenchlingIcon icon="arrow-up" className={classes.navIcon} aria-hidden="true" />
                <BenchlingIcon icon="arrow-down" className={classes.navIcon} aria-hidden="true" />
              </>
            }
            label="Navigate"
          />
          <KeyHint label="Back">Esc</KeyHint>
        </div>
        <FeedbackLink />
      </div>
    );
  }

  if (!hasNavigableResults) {
    return null;
  }

  return (
    <div className={classes.root}>
      <div className={classes.hints}>
        <IconHint
          icons={
            <>
              <BenchlingIcon icon="arrow-up" className={classes.navIcon} aria-hidden="true" />
              <BenchlingIcon icon="arrow-down" className={classes.navIcon} aria-hidden="true" />
            </>
          }
          label="Navigate"
        />
        <IconHint
          icons={
            <span className={classes.returnGlyph} aria-hidden="true">
              ↵
            </span>
          }
          label="Open"
        />
        <KeyHint label="Close">Esc</KeyHint>
      </div>
      <FeedbackLink />
    </div>
  );
}
